import type { ReactNode } from 'react';
import { cn } from '@/shared/cn';
import { vizToneColor, type VizTone } from './viz-tone';

export type KpiDeltaDirection = 'up' | 'down' | 'flat';

export type KpiCardProps = {
  label: string;
  value: string;
  unit?: string;
  sub?: string;
  delta?: {
    text: string;
    direction: KpiDeltaDirection;
    tone: VizTone;
  };
  tone: VizTone;
  className?: string;
  footer?: ReactNode;
  /** `bottom` pins the footer to the card's foot so chips share a baseline
   * across a stretched row. Default `flow` keeps it under the sub line. */
  footerAlign?: 'flow' | 'bottom';
  /** `text` sets a worded value (a list, a state) one step below the figure
   * size so it does not out-shout the numbers beside it. Default `figure`. */
  valueSize?: 'figure' | 'text';
  /** `2` reserves two label lines, the label set at the foot of that box, so
   * values in a row of cards share a baseline whichever labels wrap and each
   * label stays tight to its value. Default: the label's natural height. */
  labelLines?: 1 | 2;
};

const DELTA_GLYPH: Record<KpiDeltaDirection, string> = {
  up: '▲',
  down: '▼',
  flat: '±',
};

export function KpiCard({
  label,
  value,
  unit,
  sub,
  delta,
  tone,
  className,
  footer,
  footerAlign = 'flow',
  valueSize = 'figure',
  labelLines,
}: KpiCardProps) {
  const borderColor = vizToneColor(tone);

  return (
    <div
      className={cn('card-matte-2 flex h-full flex-col border-t-[3px] px-4 py-4', className)}
      style={{ borderTopColor: borderColor }}
    >
      <p
        className={cn(
          'text-xs font-medium uppercase tracking-wide text-muted',
          labelLines === 2 && 'flex min-h-[2lh] flex-col justify-end',
        )}
      >
        {label}
      </p>
      <p
        className={cn(
          'mt-2 font-semibold tracking-tight text-ink-primary',
          valueSize === 'text' ? 'text-lg leading-snug' : 'text-2xl',
        )}
      >
        {value}
      </p>
      {unit ? <p className="mt-0.5 text-xs text-muted">{unit}</p> : null}
      {sub ? <p className={cn('text-xs text-muted', unit ? 'mt-1' : 'mt-1')}>{sub}</p> : null}
      {delta ? (
        <p
          className="mt-1 text-xs font-medium tabular-nums"
          style={{ color: vizToneColor(delta.tone) }}
        >
          {DELTA_GLYPH[delta.direction]} {delta.text}
        </p>
      ) : null}
      {footer ? (
        <div
          className={cn(
            'flex flex-wrap gap-1.5',
            footerAlign === 'bottom' ? 'mt-auto pt-2' : 'mt-2',
          )}
        >
          {footer}
        </div>
      ) : null}
    </div>
  );
}
